const STORAGE_KEY = "athletain.interaction_events.v1";
const MAX_EVENTS = 600;
const HEATMAP_COLUMNS = 6;
const HEATMAP_ROWS = 4;

const readStorage = () => {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStorage = (events) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_EVENTS)));
  } catch {
    // Ignore storage quota issues silently; analytics should not break the app.
  }
};

const escapeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

export const getAnalyticsConfig = () => ({
  key: process.env.REACT_APP_POSTHOG_KEY || "",
  host: process.env.REACT_APP_POSTHOG_HOST || "https://app.posthog.com",
});

export const readInteractionEvents = () => readStorage();

export const persistInteractionEvent = (event) => {
  const events = [...readStorage(), event].slice(-MAX_EVENTS);
  writeStorage(events);
  return events;
};

export const createInteractionEvent = (type, payload = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  timestamp: new Date().toISOString(),
  ...payload,
});

export const getElementAnalyticsLabel = (element) => {
  if (!element || typeof element.getAttribute !== "function") {
    return "unknown";
  }

  const explicitId = element.getAttribute("data-analytics-id");
  if (explicitId) return explicitId;

  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) return escapeText(ariaLabel);

  const title = element.getAttribute("title");
  if (title) return escapeText(title);

  const text = escapeText(element.textContent);
  if (text) return text.slice(0, 80);

  const tagName = element.tagName ? element.tagName.toLowerCase() : "element";
  const role = element.getAttribute("role");
  return role ? `${tagName}[role="${role}"]` : tagName;
};

export const buildInteractionSnapshot = (events) => {
  const safeEvents = Array.isArray(events) ? events : [];

  const pageViews = safeEvents.filter((event) => event.type === "page_view");
  const clicks = safeEvents.filter((event) => event.type === "interaction_click");
  const stuckSignals = safeEvents.filter(
    (event) => event.type === "stuck_interaction" || event.type === "form_invalid",
  );

  const groupCounts = (items, keyGetter) =>
    Object.entries(
      items.reduce((acc, item) => {
        const key = keyGetter(item);
        if (!key) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    )
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "es"));

  const topPages = groupCounts(pageViews, (event) => event.view || event.page || "dashboard");
  const topInteractions = groupCounts(
    clicks,
    (event) => event.target_label || event.analytics_id || event.selector || "interaction",
  ).slice(0, 8);

  const heatmapCells = Array.from({ length: HEATMAP_COLUMNS * HEATMAP_ROWS }, (_, index) => ({
    col: index % HEATMAP_COLUMNS,
    row: Math.floor(index / HEATMAP_COLUMNS),
    count: 0,
  }));

  clicks.forEach((event) => {
    const x = Number(event.x_percent);
    const y = Number(event.y_percent);

    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    const col = Math.min(
      HEATMAP_COLUMNS - 1,
      Math.max(0, Math.floor(x * HEATMAP_COLUMNS)),
    );
    const row = Math.min(HEATMAP_ROWS - 1, Math.max(0, Math.floor(y * HEATMAP_ROWS)));
    const cellIndex = row * HEATMAP_COLUMNS + col;
    heatmapCells[cellIndex].count += 1;
  });

  const recentEvents = safeEvents.slice(-12).reverse();

  return {
    totals: {
      pageViews: pageViews.length,
      clicks: clicks.length,
      stuckSignals: stuckSignals.length,
      totalEvents: safeEvents.length,
    },
    topPages,
    topInteractions,
    heatmapCells,
    recentEvents,
    hasData: safeEvents.length > 0,
  };
};

export const getTargetPayload = (element, currentView) => ({
  analytics_id:
    element?.getAttribute?.("data-analytics-id") ||
    element?.getAttribute?.("aria-label") ||
    element?.getAttribute?.("title") ||
    "",
  selector: element?.tagName ? element.tagName.toLowerCase() : "element",
  target_label: getElementAnalyticsLabel(element),
  view: currentView || "unknown",
});

